import * as ko from "knockout";

import { FeatureType, Feature } from "./api";
import * as L from "../mapbox";


export type LayerAddedCallback = (e: FeatureLayerEvent<L.FeatureGroup<L.ILayer>>) => void;


export interface GeoJSONLayer<TLayer extends L.ILayer> extends L.FeatureGroup<TLayer> {
    // Internal id used to reference this layer
    _leaflet_id: number;

    // The GeoJSON feature contained in this layer.
    feature: GeoJSON.Feature<GeoJSON.GeometryObject>;

    // Binds a popup with a particular HTML content to a click on this layer. You
    // can also open the bound popup with the openPopup method.
    bindPopup(html: string, options?: L.PopupOptions): L.FeatureGroup<TLayer>;

    // Opens the popup previously bound by the bindPopup method.
    openPopup(): L.FeatureGroup<TLayer>;

    // Closes the bound popup if it's opened.
    closePopup(): L.FeatureGroup<TLayer>;

    // Unbinds a previously bound popup.
    unbindPopup(): L.FeatureGroup<TLayer>;
}


export interface MapFeature<T> extends L.FeatureGroup<any> {
    properties: T;
}


export interface GeoArrayLayer<TLayer extends L.ILayer> extends L.FeatureGroup<TLayer> {
    // Internal id used to reference this layer
    _leaflet_id: number;

    // The Marker feature contained in this layer
    feature: MapFeature<any>;

    // Binds a popup with a particular HTML content to a click on this layer. You
    // can also open the bound popup with the openPopup method.
    bindPopup(html: string, options?: L.PopupOptions): L.FeatureGroup<TLayer>;

    // Opens the popup previously bound by the bindPopup method.
    openPopup(): L.FeatureGroup<TLayer>;

    // Closes the bound popup if it's opened.
    closePopup(): L.FeatureGroup<TLayer>;

    // Unbinds a previously bound popup.
    unbindPopup(): L.FeatureGroup<TLayer>;
}


export interface FeatureLayerEvent<TLayer extends L.FeatureGroup<L.ILayer>> extends L.LeafletEvent {
    // The layer that was added or removed.
    layer: TLayer;
}

interface FeaturePopupEvent<TLayer extends L.FeatureGroup<L.ILayer>> extends L.LeafletPopupEvent {
    // The layer that is associated with the popup
    layer: TLayer;
}

/**
 * Creates a Pli Map Feature from a Mapbox layer
 */
export function createFeature<TLayer extends L.ILayer>(layer: GeoJSONLayer<TLayer> | GeoArrayLayer<TLayer>, map: L.Map, type: FeatureType): Feature {
    let contentSubscription: ko.subscription<any> = null;
    let popupLayer: GeoJSONLayer<TLayer> | GeoArrayLayer<TLayer> = null;

    return {
        _internalId: layer._leaflet_id,
        properties: layer.feature.properties,
        type: type,
        bindPopup: (content, options?) => {
            const leafletOptions: L.PopupOptions = {
                className: options && options.customLayout ? "mapping-custom-popup" : "",
                closeButton: options && (options.closeButton === true || options.closeButton === undefined)
            };

            // TODO: When leaflet 1.0 is released, it will accept a function for content
            if (typeof content === "string") {
                layer.bindPopup(content, leafletOptions);
            } else {
                if (contentSubscription) { contentSubscription.dispose(); }
                contentSubscription = content.subscribe(function (html): void {
                    this.unbindPopup();
                    this.bindPopup(html, options);
                }, layer);
                layer.bindPopup(content(), leafletOptions);
            }

            const closeButton = options && options.closeButton;
            if (typeof closeButton === "string") {
                layer.on("popupopen", (popupEvent: FeaturePopupEvent<GeoJSONLayer<TLayer>>) => {
                    if (!map) { return; } // Map not yet rendered, do nothing
                    const container$ = map.getContainer();
                    const closeButton$ = <HTMLElement>container$.querySelector(closeButton);

                    // If multi-polygon group feature, e.layer will be the polygon with the popup.
                    popupLayer = popupEvent.layer || layer;

                    if (closeButton$) {
                        function closePopup(closeEvent: Event): void {
                            L.DomEvent.preventDefault(closeEvent);
                            popupLayer.closePopup();
                            L.DomEvent.off(closeButton$, "click", closePopup);
                        }
                        L.DomEvent.on(closeButton$, "click", closePopup);
                    }
                });
            }
        },
        openPopup: () => { map.closePopup(); popupLayer.openPopup(); },
        closePopup: () => { popupLayer.closePopup(); },
        unbindPopup: () => {
            if (contentSubscription !== null) {
                contentSubscription.dispose();
                contentSubscription = null;
            }
            layer.closePopup();
            layer.unbindPopup();
        },
        on: layer.on.bind(layer),
        off: layer.off.bind(layer),
        once: layer.once.bind(layer)
    };
}

export interface ColorWheel {
    (): string;
}

export enum ClusterPlugin {
    MarkerCluster,
    SuperCluster
}



// WEBPACK FOOTER //
// common.ts